import { getApiBaseUrl } from '../lib/getApiUrl';

// Google Places API service for address parsing and ZIP code lookup
export interface GoogleAddressResult {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  fullAddress: string;
  place_id?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

export class GoogleAddressService {
  // Resolve API base at call time to avoid TDZ/cycles
  private static getApiBase(): string {
    return getApiBaseUrl();
  }

  /**
   * Get address details with ZIP code using Google Places API
   * @param address - Full address string
   * @returns Promise with parsed address including ZIP code
   */
  static async getAddressWithZip(address: string): Promise<GoogleAddressResult | null> {
    try {
      console.log('🔍 Looking up address with Google Places API:', address);
      
      // Use backend proxy to find the address
      const response = await fetch(
        `${this.getApiBase()}/api/places/findplace?input=${encodeURIComponent(address)}`
      );

      if (!response.ok) {
        throw new Error(`Backend API error: ${response.status}`);
      }

      const data = await response.json();
      console.log('📍 Google Places API response:', data);

      if (data.status !== 'OK' || !data.candidates?.[0]) {
        console.log('❌ No address found in Google Places API');
        return null;
      }

      const place = data.candidates[0];
      
      // Get detailed place information including address components
      const detailsResponse = await fetch(
        `${this.getApiBase()}/api/places/details?place_id=${place.place_id}`
      );

      if (!detailsResponse.ok) {
        throw new Error(`Backend API error: ${detailsResponse.status}`);
      }

      const detailsData = await detailsResponse.json();
      console.log('📍 Google Place Details response:', detailsData);

      if (detailsData.status !== 'OK' || !detailsData.result) {
        console.log('❌ No place details found');
        return null;
      }

      const placeDetails = detailsData.result;
      const addressComponents = placeDetails.address_components || [];

      // Extract address components
      const streetNumber = addressComponents.find(c => c.types.includes('street_number'))?.long_name || '';
      const route = addressComponents.find(c => c.types.includes('route'))?.long_name || '';
      const city = addressComponents.find(c => c.types.includes('locality'))?.long_name || '';
      const state = addressComponents.find(c => c.types.includes('administrative_area_level_1'))?.short_name || '';
      const zipCode = addressComponents.find(c => c.types.includes('postal_code'))?.long_name || '';

      const street = streetNumber && route ? `${streetNumber} ${route}` : route;

      const result: GoogleAddressResult = {
        street: street || '',
        city: city || '',
        state: state || '',
        zipCode: zipCode || '',
        fullAddress: placeDetails.formatted_address || address,
        place_id: place.place_id,
        coordinates: placeDetails.geometry?.location ? {
          lat: placeDetails.geometry.location.lat,
          lng: placeDetails.geometry.location.lng
        } : undefined
      };

      console.log('✅ Parsed address with Google Places API:', result);
      return result;

    } catch (error) {
      console.error('❌ Google Places API error:', error);
      return null;
    }
  }

  /**
   * Get address autocomplete suggestions
   * @param input - User input string
   * @returns Promise with autocomplete predictions
   */
  static async getAutocompleteSuggestions(input: string): Promise<any[]> {
    try {
      const trimmedInput = input.trim(); // Trim input to avoid %20 in URL
      console.log('🔍 GoogleAddressService: Making autocomplete request for:', trimmedInput);
      const apiBase = this.getApiBase();
      console.log('🔗 GoogleAddressService: API_BASE_URL:', apiBase);
      
      const url = `${apiBase}/api/autocomplete?input=${encodeURIComponent(trimmedInput)}`;
      console.log('🔗 GoogleAddressService: Full URL:', url);
      
      const response = await fetch(url);
      
      console.log('📊 GoogleAddressService: Response status:', response.status);
      console.log('📊 GoogleAddressService: Response ok:', response.ok);

      if (!response.ok) {
        throw new Error(`Backend API error: ${response.status}`);
      }

      const data = await response.json();
      console.log('📍 GoogleAddressService: Response data:', data);
      
      if (data.status === 'OK' && data.predictions) {
        console.log('✅ GoogleAddressService: Returning', data.predictions.length, 'predictions');
        return data.predictions;
      }

      console.log('❌ GoogleAddressService: No predictions found in response');
      return [];
    } catch (error) {
      console.error('❌ GoogleAddressService: Autocomplete API error:', error);
      return [];
    }
  }

  /**
   * Get place details from place_id
   * @param placeId - Google Place ID
   * @returns Promise with place details
   */
  static async getPlaceDetails(placeId: string): Promise<GoogleAddressResult | null> {
    try {
      const response = await fetch(
        `${this.getApiBase()}/api/places/details?place_id=${placeId}`
      );

      if (!response.ok) {
        throw new Error(`Backend API error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status !== 'OK' || !data.result) {
        return null;
      }

      const place = data.result;
      const addressComponents = place.address_components || [];

      // Extract address components
      const streetNumber = addressComponents.find(c => c.types.includes('street_number'))?.long_name || '';
      const route = addressComponents.find(c => c.types.includes('route'))?.long_name || '';
      const city = addressComponents.find(c => c.types.includes('locality'))?.long_name || '';
      const state = addressComponents.find(c => c.types.includes('administrative_area_level_1'))?.short_name || '';
      const zipCode = addressComponents.find(c => c.types.includes('postal_code'))?.long_name || '';

      const street = streetNumber && route ? `${streetNumber} ${route}` : route;

      return {
        street: street || '',
        city: city || '',
        state: state || '',
        zipCode: zipCode || '',
        fullAddress: place.formatted_address || '',
        place_id: place.place_id,
        coordinates: place.geometry?.location ? {
          lat: place.geometry.location.lat,
          lng: place.geometry.location.lng
        } : undefined
      };

    } catch (error) {
      console.error('❌ Google Place Details API error:', error);
      return null;
    }
  }
} 